import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { RefreshCw } from "lucide-react";

export function StatCard({ icon: Icon, label, value, sub, tone = "turq", testid }) {
  const tones = {
    turq: "bg-turq-100 text-turq-900 dark:bg-surface2 dark:text-turq-300",
    coral: "bg-coral-100 text-coral-900 dark:bg-surface2 dark:text-coral-300",
    beige: "bg-beige-600 text-turq-900 dark:bg-surface2 dark:text-turq-300",
  };
  return (
    <div data-testid={testid} className="bg-surface rounded-2xl p-5 shadow-md shadow-turq-900/5 border border-brd/50 animate-fade-up hover-lift">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${tones[tone]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="font-head font-black text-2xl">{value}</div>
      <div className="text-xs text-mutext mt-1">{label}</div>
      {sub && <div className="text-[11px] text-coral-600 font-bold mt-1.5">{sub}</div>}
    </div>
  );
}

const TIER_STYLES = {
  small: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/60 dark:text-amber-200 dark:border-amber-800",
  medium: "bg-teal-100 text-teal-800 border-teal-300 dark:bg-teal-950/60 dark:text-teal-200 dark:border-teal-800",
  large: "bg-cyan-100 text-cyan-900 border-cyan-300 dark:bg-cyan-950/60 dark:text-cyan-200 dark:border-cyan-800",
};
const TIER_LABEL = { small: "الشريحة الصغرى", medium: "الشريحة المتوسطة", large: "الشريحة الكبرى" };

export function TierBadge({ tier }) {
  if (!tier) return <span className="text-xs text-mutext">—</span>;
  return (
    <span data-testid={`tier-badge-${tier}`}
      className={`text-[11px] px-3 py-1 rounded-full font-bold border ${TIER_STYLES[tier]}`}>
      {TIER_LABEL[tier]}
    </span>
  );
}

export function Gauge({ pct, target = 90, size = 170 }) {
  const p = Math.max(0, Math.min(100, pct || 0));
  const r = (size - 20) / 2;
  const circ = 2 * Math.PI * r;
  const reached = p >= target;
  return (
    <div data-testid="empowerment-gauge" className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface2)" strokeWidth="14" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={reached ? "#12939E" : "#FF7A59"} strokeWidth="14" strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={circ - (circ * p) / 100}
          style={{ transition: "stroke-dashoffset 1s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span data-testid="empowerment-gauge-value" className="font-head font-black text-4xl text-turq-900 dark:text-turq-300">{p}%</span>
        <span className="text-[11px] text-mutext">من المستهدف {target}%</span>
      </div>
    </div>
  );
}

export function CountUp({ value, suffix = "", testid }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const target = Number(value) || 0;
    const start = performance.now();
    const dur = 1200;
    const tick = (t) => {
      const k = Math.min(1, (t - start) / dur);
      setDisplay(Math.round(target * (1 - Math.pow(1 - k, 3))));
      if (k < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value]);
  return (
    <span ref={ref} data-testid={testid}>
      {display.toLocaleString("en-US")}{suffix}
    </span>
  );
}

export function MotiBanner({ phrase, onRefresh }) {
  return (
    <div data-testid="motivational-banner"
      className="rounded-3xl p-6 mb-6 text-white relative overflow-hidden"
      style={{ background: "linear-gradient(100deg,#0B6E7A,#12939E 55%,#FF7A59)" }}>
      <div className="absolute -left-10 -top-14 w-48 h-48 rounded-full bg-white/10" />
      <div className="absolute left-24 -bottom-16 w-32 h-32 rounded-full bg-white/10" />
      <div className="flex items-center justify-between gap-4 relative">
        <p className="font-head font-bold text-base sm:text-lg leading-relaxed max-w-2xl">{phrase}</p>
        {onRefresh && (
          <button data-testid="phrase-refresh-btn" onClick={onRefresh}
            className="bg-white/15 hover:bg-white/25 transition-colors text-xs px-4 py-2 rounded-full flex items-center gap-1.5 whitespace-nowrap">
            <RefreshCw className="w-3.5 h-3.5" /> عبارة أخرى
          </button>
        )}
      </div>
    </div>
  );
}

export function EmptyState({ text, testid }) {
  return (
    <div data-testid={testid} className="bg-surface rounded-2xl border border-dashed border-brd p-10 text-center text-sm text-mutext">
      {text}
    </div>
  );
}

import { X } from "lucide-react";

export function PageHeader({ title, sub, children }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
      <div>
        <h1 className="font-head font-bold text-xl text-ink">{title}</h1>
        {sub && <p className="text-xs text-mutext mt-1">{sub}</p>}
      </div>
      {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

export const inputCls =
  "w-full rounded-xl border border-brd bg-surface px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-turq-600/40 transition-shadow";
export const btnPrimary =
  "rounded-xl px-4 py-2.5 text-sm font-bold text-white bg-coral-600 hover:bg-coral-900 transition-colors flex items-center gap-2 disabled:opacity-50";
export const btnTurq =
  "rounded-xl px-4 py-2.5 text-sm font-bold text-white bg-turq-600 hover:bg-turq-900 transition-colors flex items-center gap-2 disabled:opacity-50";
export const btnGhost =
  "rounded-xl px-4 py-2.5 text-sm font-bold text-turq-900 dark:text-turq-300 bg-surface shadow-md hover:shadow-lg transition-shadow flex items-center gap-2";

export function Modal({ open, onClose, title, children, wide, testid }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/45 backdrop-blur-sm" onClick={onClose}>
      <div data-testid={testid}
        className={`bg-surface rounded-2xl shadow-2xl border border-brd w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[85vh] overflow-auto animate-fade-up`}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-brd sticky top-0 bg-surface z-10">
          <h3 className="font-head font-bold text-base">{title}</h3>
          <button data-testid={`${testid || "modal"}-close-btn`} onClick={onClose} className="text-mutext hover:text-ink transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

export function EmpowermentBar({ pct, volunteers, total, target = 90, scope = "في جميع المدارس", gender, testid = "empowerment-bar" }) {
  const p = Math.max(0, Math.min(100, pct || 0));
  const required = Math.ceil((total || 0) * target / 100);
  const remaining = Math.max(0, required - (volunteers || 0));
  const girls = gender === "بنات";
  return (
    <div data-testid={testid} className="bg-surface rounded-3xl border border-brd shadow-md p-6">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <h3 className="font-head font-bold text-base">مؤشر التمكين المستهدف — مشاركة {target}% من الطلبة</h3>
        <span className={`font-head font-black text-2xl ${p >= target ? "text-turq-600" : "text-coral-600"}`}>{p}%</span>
      </div>
      <p className="text-[11px] text-mutext mb-5">
        {girls ? "المتطوعات الفعليات" : "المتطوعون الفعليون"} {volunteers} من أصل {total} {girls ? "طالبةً" : "طالباً"} {scope}
      </p>
      <div className="relative h-3.5 rounded-full bg-surface2">
        <div className="absolute inset-y-0 right-0 rounded-full transition-all duration-700"
          style={{ width: `${p}%`, background: "linear-gradient(90deg,#FF7A59,#12939E)" }} />
        <div className="absolute -top-1.5 -bottom-1.5 w-[3px] bg-coral-900 dark:bg-coral-300 rounded"
          style={{ right: `${target}%` }} />
        <span className="absolute -top-6 text-[10px] font-bold text-coral-900 dark:text-coral-300 whitespace-nowrap"
          style={{ right: `calc(${target}% - 1.5rem)` }}>
          المستهدف {target}%
        </span>
      </div>
      <div className="flex justify-between text-[10px] text-mutext mt-1.5">
        <span>0%</span>
        <span>100%</span>
      </div>
      {remaining > 0 ? (
        <p className="text-[11px] text-center text-mutext mt-2.5">
          يتبقى <b className="text-coral-600">{remaining.toLocaleString("en-US")}</b> {girls ? "طالبةً متطوعةً" : "طالباً متطوعاً"} لبلوغ المستهدف (المطلوب {required.toLocaleString("en-US")} من {total.toLocaleString("en-US")})
        </p>
      ) : (
        <p className="text-[11px] text-center font-bold text-turq-600 mt-2.5">تم بلوغ المستهدف — أداء مشرّف يستحق التقدير</p>
      )}
    </div>
  );
}
